import { Alert } from '@components'
import { formatRelativeTime } from '@utils'
import { tryFormatIBAN } from '@utils/iban'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaSyncAlt } from 'react-icons/fa'
import styled from 'styled-components'

interface Props {
  accountId: number
  accountName?: string
  accountIdentifier: string
  lastSyncAt: string
  isMissingInfo: boolean
  ownerId?: number
  adminIds?: number[]
  renderFooter?: () => React.ReactNode
}

export const BankAccount: React.FC<Props> = ({
  accountId,
  accountName,
  accountIdentifier,
  lastSyncAt,
  isMissingInfo,
  renderFooter
}) => {
  const [t] = useTranslation()

  return (
    <>
      <AccountComponent key={accountId}>
        <AccountRow>
          <AccountName>
            {accountName ?? t('settings.integrations.bank.defaultAccountName')}
            <IBAN>{tryFormatIBAN(accountIdentifier)}</IBAN>
          </AccountName>
        </AccountRow>
        {renderFooter && <AccountRow>{renderFooter()}</AccountRow>}

        {!isMissingInfo && <AccountAlert lastSyncAt={lastSyncAt} />}

        <Alert
          type="warning"
          isVisible={isMissingInfo}
          description={t('settings.integrations.bank.accountEditButtonMissingInfo')}
        />
      </AccountComponent>
    </>
  )
}
interface AccountAlertProps {
  lastSyncAt: string
}

const AccountAlert: React.FC<AccountAlertProps> = ({ lastSyncAt }) => {
  const [t] = useTranslation()

  let alertText = t('settings.integrations.bank.accountActive')
  let alertType = 'success'
  let AlertIcon = FaCheck

  if (lastSyncAt) {
    alertText = t('settings.integrations.bank.accountSynced', {
      syncedAt: formatRelativeTime(lastSyncAt)
    })
    alertType = 'success'
    AlertIcon = FaSyncAlt
  }

  return alertText ? (
    <StyledAccountAlert className={alertType}>
      <AlertIcon /> {alertText}
    </StyledAccountAlert>
  ) : (
    <></>
  )
}

const AccountComponent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const IBAN = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.metalGray};
`

const AccountRow = styled.div`
  display: flex;
`

const AccountName = styled.div`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: bold;
`

const StyledAccountAlert = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &.success {
    color: ${({ theme }) => theme.colors.success};
  }

  &.warning {
    color: ${({ theme }) => theme.colors.warning};
  }
`
